import React from 'react';

import type { IntlShape } from 'react-intl';

import type { PortalProviderAPI } from '@atlaskit/editor-common/common';
import type { EventDispatcher } from '@atlaskit/editor-common/event-dispatcher';
import type { MediaProvider } from '@atlaskit/editor-common/provider-factory/media-provider';
import type ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';
import { WithProviders } from '@atlaskit/editor-common/provider-factory/with-providers';
import ReactNodeView from '@atlaskit/editor-common/react-node-view';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import {
	type NamedPluginStatesFromInjectionAPI,
	useSharedPluginStateWithSelector,
} from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorView, NodeView } from '@atlaskit/editor-prosemirror/view';

import type { MediaNextEditorPluginType } from '../mediaPluginType';
import type {
	ForwardRef,
	getPosHandler,
	getPosHandlerNode,
	MediaOptions,
	MediaPluginOptions,
} from '../types';
import { useMediaProvider } from '../ui/hooks/useMediaProvider';
import { MediaSSRReactContextsProvider } from '../ui/MediaSSRReactContextsProvider';
import { MediaGroupNext } from './mediaGroupNext';

interface MediaGroupNodeViewProps {
	allowLazyLoading?: boolean;
	intl?: IntlShape;
	isCopyPasteEnabled?: boolean;
	mediaOptions: MediaOptions;
	pluginInjectionApi: ExtractInjectionAPI<MediaNextEditorPluginType> | undefined;
	providerFactory: ProviderFactory;
}

interface RenderFn {
	editorDisabled?: boolean;
	editorViewMode?: 'view' | 'edit';
	mediaProvider?: MediaProvider | null;
}

interface MediaGroupNodeViewInternalProps {
	pluginInjectionApi: ExtractInjectionAPI<MediaNextEditorPluginType> | undefined;
	renderFn: (props: RenderFn) => JSX.Element | null;
}

const selector = (
	states: NamedPluginStatesFromInjectionAPI<
		ExtractInjectionAPI<MediaNextEditorPluginType>,
		'editorViewMode' | 'editorDisabled'
	>,
) => {
	return {
		editorDisabled: states.editorDisabledState?.editorDisabled,
		editorViewMode: states.editorViewModeState?.mode,
	};
};

function MediaGroupNodeViewInternal({
	renderFn,
	pluginInjectionApi,
}: MediaGroupNodeViewInternalProps) {
	const { editorDisabled, editorViewMode } = useSharedPluginStateWithSelector(
		pluginInjectionApi,
		['editorDisabled', 'editorViewMode'],
		selector,
	);

	const mediaProvider = useMediaProvider(pluginInjectionApi);
	return renderFn({
		editorDisabled,
		editorViewMode,
		mediaProvider,
	});
}

class MediaGroupNodeView extends ReactNodeView<MediaGroupNodeViewProps> {
	render(props: MediaGroupNodeViewProps, forwardRef: ForwardRef) {
		const { providerFactory, mediaOptions, pluginInjectionApi, intl } = props;
		const getPos = this.getPos as getPosHandlerNode;

		return (
			<MediaSSRReactContextsProvider intl={intl}>
				<WithProviders
					// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
					providers={['contextIdentifierProvider']}
					providerFactory={providerFactory}
					// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
					renderNode={({ contextIdentifierProvider }) => {
						const renderFn = ({
							mediaProvider: mediaProviderFromState,
							editorDisabled,
							editorViewMode,
						}: RenderFn) => {
							const mediaProvider = mediaProviderFromState
								? Promise.resolve(mediaProviderFromState)
								: undefined;
							return (
								<MediaGroupNext
									node={this.node}
									getPos={getPos}
									view={this.view}
									forwardRef={forwardRef}
									disabled={editorDisabled}
									allowLazyLoading={mediaOptions.allowLazyLoading}
									mediaProvider={mediaProvider}
									contextIdentifierProvider={contextIdentifierProvider}
									isCopyPasteEnabled={mediaOptions.isCopyPasteEnabled}
									anchorPos={this.view.state.selection.$anchor.pos}
									headPos={this.view.state.selection.$head.pos}
									mediaOptions={mediaOptions}
									editorViewMode={editorViewMode === 'view'}
								/>
							);
						};

						return (
							<MediaGroupNodeViewInternal
								renderFn={renderFn}
								pluginInjectionApi={pluginInjectionApi}
							/>
						);
					}}
				/>
			</MediaSSRReactContextsProvider>
		);
	}
}

export const ReactMediaGroupNode =
	(
		portalProviderAPI: PortalProviderAPI,
		eventDispatcher: EventDispatcher,
		providerFactory: ProviderFactory,
		mediaOptions: MediaPluginOptions | undefined = {},
		pluginInjectionApi: ExtractInjectionAPI<MediaNextEditorPluginType> | undefined,
		intl?: IntlShape,
	) =>
	(node: PMNode, view: EditorView, getPos: getPosHandler): NodeView => {
		return new MediaGroupNodeView(node, view, getPos, portalProviderAPI, eventDispatcher, {
			providerFactory,
			mediaOptions,
			pluginInjectionApi,
			intl,
		}).init();
	};
